import { useGetCountriesLazyQuery } from "@graphql/generated";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import SearchableSelector, { Option } from "../SearchableSelector";

interface Country {
  value: string;
  label: string;
  code: string;
}

const PREFERRED_COUNTRIES: Country[] = [
  { value: "194", label: "Saudi Arabia", code: "sa" },
  { value: "2", label: "United Arab Emirates", code: "ae" },
  { value: "107", label: "Iraq", code: "iq" },
  { value: "113", label: "Jordan", code: "jo" },
  { value: "188", label: "Qatar", code: "qa" },
  { value: "38", label: "Canada", code: "ca" },
  { value: "234", label: "United States", code: "us" },
  { value: "77", label: "United Kingdom", code: "gb" },
];

interface CountrySelectorProps {
  selectedCountry?: string;
  onSelect: (value: string) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

const CountrySelector = ({ selectedCountry, onSelect, scrollContainerRef }: CountrySelectorProps) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMoreCountries, setHasMoreCountries] = useState(true);
  const isLoadingMore = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [fetchCountries, { loading, data }] = useGetCountriesLazyQuery({
    variables: {
      first: 20,
      name: searchTerm,
    },
    fetchPolicy: "cache-and-network",
  });

  const loadMoreCountries = useCallback(() => {
    if (data?.common?.countries?.pageInfo.endCursor && !isLoadingMore.current) {
      isLoadingMore.current = true;
      fetchCountries({
        variables: {
          first: 20,
          name: searchTerm,
          after: data.common.countries.pageInfo.endCursor,
        },
      });
    }
  }, [data, fetchCountries, searchTerm]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasMoreCountries || loading) return;

      const scrollContainer = scrollContainerRef?.current || containerRef.current;
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        loadMoreCountries();
      }
    };

    const scrollContainer = scrollContainerRef?.current || containerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [hasMoreCountries, loading, loadMoreCountries, scrollContainerRef]);

  useEffect(() => {
    if (data?.common?.countries?.edges) {
      const newCountries = data.common.countries.edges.map((edge) => ({
        value: edge?.node?.id ?? "",
        label: edge?.node?.name ?? "",
        code: edge?.node?.code2?.toLowerCase() ?? "",
      }));

      const filteredCountries = !searchTerm
        ? newCountries.filter((country) => !PREFERRED_COUNTRIES.some((preferred) => preferred.value === country.value))
        : newCountries;

      if (isLoadingMore.current) {
        setCountries((prevCountries) => {
          const existingIds = new Set(prevCountries.map((c) => c.value));
          const uniqueNewCountries = filteredCountries.filter((c) => !existingIds.has(c.value));
          return [...prevCountries, ...uniqueNewCountries];
        });
      } else {
        setCountries(searchTerm ? filteredCountries : [...PREFERRED_COUNTRIES, ...filteredCountries]);
      }

      setHasMoreCountries(!!data.common.countries.pageInfo.hasNextPage);
      isLoadingMore.current = false;
    }
  }, [data, searchTerm]);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    isLoadingMore.current = false;
    setCountries([]);
    fetchCountries({
      variables: {
        first: 20,
        name: search,
        after: "",
      },
    });
  };

  const renderCountryItem = (option: Option & Partial<Country>) => (
    <div className="flex flex-col items-center gap-2 w-full h-full">
      <div className="flex-shrink-0">
        <img
          src={`https://flagcdn.com/16x12/${option.code}.png`}
          srcSet={`https://flagcdn.com/32x24/${option.code}.png 2x, https://flagcdn.com/48x36/${option.code}.png 3x`}
          width="16"
          height="12"
          className="w-6 h-4"
          alt={option.label}
        />
      </div>
      <div className="truncate text-sm">{option.label}</div>
    </div>
  );

  return (
    <div ref={containerRef}>
      <motion.h2
        className="text-xl lg:text-3xl font-bold mb-4 md:mb-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        Where are you living now?
      </motion.h2>
      <div className="w-full mx-auto">
        <SearchableSelector
          options={countries}
          selectedValue={selectedCountry}
          onSelect={onSelect}
          placeholder="Search for a country..."
          loading={loading}
          onSearch={handleSearch}
          hasMore={hasMoreCountries}
          virtualized={true}
          renderOption={renderCountryItem}
          gridLayout={true}
          containerScrolling={true}
        />
      </div>
    </div>
  );
};

export default CountrySelector;
